import { FontWeight, Heading } from '@lili/lili-frontend-common';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../../components/Layout/Layout';
import { FEATURE_FLAG } from '../../../constants/general';
import MainContent from '../../../generic-components/Layout/MainContent';
import SubContent from '../../../generic-components/Layout/SubContent';
import { useBalanceUpOptInMutation } from '../../../hooks/mutations/useBalanceUpOptInMutation';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { navigateToRelevantAddMoney, VIEWS_MAP } from '../../../router/constants';
import bugsnagHandler from '../../../services/bugsnag.service';
import { featureFlagMightBeAvailable, validateFeatureFlag } from '../../../services/featureFlagService';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { useStore } from '../../../store';
import BalanceUpDashboardSubContent from '../components/BalanceUpDashboardSubContent';
import BalanceUpStepSwitch from '../components/BalanceUpStepSwitch';
import { BALANCE_UP_STEPS_MAP, ENROLLMENT_TYPES } from '../constants';
import { OverdraftDetails, OverdraftState } from '../../../types';
import { useGetSingleInAppComponentLog } from '../../../hooks/utilities/useGetSingleInAppComponentLog';
import { InAppComponent } from '../../../hooks/query/useGetInappComponentsLogs';
import { useUpdateInAppComponent } from '../../../hooks/mutations/useUpdateInAppComponentsLogs';

export const BalanceUpContext = createContext<{ isLoadingState: boolean; overDraftObj?: OverdraftDetails }>({
  isLoadingState: false,
});

const BalanceUp = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('balanceUpTranslation');
  const { getPrimaryAccount } = useStore((state) => state);
  const {
    customer: { externalId },
  } = useStore((state) => state.summary);
  const overdraftObject = getPrimaryAccount().overdraft;
  const { achAccountNumber } = getPrimaryAccount();
  const [activeStep, setActiveStep] = useState<number>(BALANCE_UP_STEPS_MAP.ZERO_STATE);
  const [isSubContentShown, setSubContentShown] = useState<boolean>(false);
  const [enrollmentType, setEnrollmentType] = useState<string>('');
  const {
    data: appComponentLog,
    isLoading: isAppComponentLogLoading,
    refetch: refetchAppComponentLog,
  } = useGetSingleInAppComponentLog(externalId, InAppComponent.OVERDRAFT_ELIGIBLE_FIRST_TIME);
  const { mutateAsync: updateInAppComponentLogMutation } = useUpdateInAppComponent();

  const { refetch: fetchSummaryData, isRefetching: isLoadingSummary } = useAccountSummaryQuery(() => {
    if (activeStep === BALANCE_UP_STEPS_MAP.ENROLLMENT) {
      setActiveStep(BALANCE_UP_STEPS_MAP.BALANCE_UP_DASHBOARD);
    }
  });

  const { mutate, isLoading: isLoadingOptIn } = useBalanceUpOptInMutation({
    customerId: externalId,
    achAccountNumber,
    optIn: true,
    handleOnSuccess: async () => {
      await fetchSummaryData();
    },
  });

  const isLoadingState = useMemo(() => isLoadingSummary || isLoadingOptIn, [isLoadingSummary, isLoadingOptIn]);

  const contextData = useMemo(
    () => ({ isLoadingState, overDraftObj: overdraftObject }),
    [isLoadingState, overdraftObject],
  );

  const getActiveStep = () => {
    const { state } = overdraftObject;
    let step: number = BALANCE_UP_STEPS_MAP.ZERO_STATE;

    if (!validateFeatureFlag(FEATURE_FLAG.BALANCE_UP) && featureFlagMightBeAvailable(FEATURE_FLAG.BALANCE_UP)) {
      setEnrollmentType(ENROLLMENT_TYPES.ACC_UPGRADE_REQ);
      step = BALANCE_UP_STEPS_MAP.ENROLLMENT;
    } else if (state === OverdraftState.NOT_ELIGIBLE) {
      setEnrollmentType(ENROLLMENT_TYPES.ACH_TRANSFER_REQ);
      step = BALANCE_UP_STEPS_MAP.ENROLLMENT;
    } else if (appComponentLog?.doNotShowAgain !== true) {
      setEnrollmentType(ENROLLMENT_TYPES.HAPPY_FLOW_ENROLL);
      step = BALANCE_UP_STEPS_MAP.ENROLLMENT;
    } else if (
      (state === OverdraftState.OPTED_OUT || state === OverdraftState.OPTED_IN || state === OverdraftState.REVOKED) &&
      appComponentLog?.doNotShowAgain === true
    ) {
      step = BALANCE_UP_STEPS_MAP.BALANCE_UP_DASHBOARD;
    } else {
      step = BALANCE_UP_STEPS_MAP.ZERO_STATE;
      bugsnagHandler.notify(new Error('BalanceUp invalid user state'), (e: any) => {
        e.severity = 'info';
        e.context = 'component-BalanceUp';
        e.addMetadata({ ...overdraftObject });
      });
    }
    setActiveStep(step);
  };

  const handleStepSubmit = async (type: number) => {
    switch (type) {
      case BALANCE_UP_STEPS_MAP.ENROLLMENT:
        if (enrollmentType === ENROLLMENT_TYPES.HAPPY_FLOW_ENROLL) {
          await updateInAppComponentLogMutation({
            customerId: externalId,
            body: {
              name: InAppComponent.OVERDRAFT_ELIGIBLE_FIRST_TIME,
              doNotShowAgain: true,
            },
          });
          await refetchAppComponentLog();
          await mutate();

          setActiveStep(BALANCE_UP_STEPS_MAP.BALANCE_UP_DASHBOARD);
        } else if (enrollmentType === ENROLLMENT_TYPES.ACH_TRANSFER_REQ) {
          navigateToRelevantAddMoney(navigate);
        }
        break;
      default:
        setActiveStep(BALANCE_UP_STEPS_MAP.ZERO_STATE);
    }
  };

  useEffect(() => {
    const shouldShowEligibilityNotice =
      activeStep === BALANCE_UP_STEPS_MAP.BALANCE_UP_DASHBOARD ||
      (activeStep === BALANCE_UP_STEPS_MAP.ENROLLMENT &&
        overdraftObject.eligible &&
        validateFeatureFlag(FEATURE_FLAG.BALANCE_UP) &&
        featureFlagMightBeAvailable(FEATURE_FLAG.BALANCE_UP));
    switch (activeStep) {
      case BALANCE_UP_STEPS_MAP.ZERO_STATE:
        break;
      case BALANCE_UP_STEPS_MAP.ENROLLMENT:
        trackMixPanel('View BalanceUp Enrollment Screen', {
          Origin: 'Dashboard',
          'Is Eligible?': overdraftObject.eligible,
        });
        break;
      case BALANCE_UP_STEPS_MAP.BALANCE_UP_DASHBOARD:
        trackMixPanel('View BalanceUp Screen', {});
        break;
      default:
        break;
    }
    setSubContentShown(shouldShowEligibilityNotice);
  }, [activeStep]);

  useEffect(() => {
    if (!validateFeatureFlag(FEATURE_FLAG.BALANCE_UP) && !featureFlagMightBeAvailable(FEATURE_FLAG.BALANCE_UP)) {
      navigate(`/${VIEWS_MAP.DASHBOARD}`);
      return;
    }
    if (isAppComponentLogLoading || isLoadingSummary) return;
    getActiveStep();
  }, [appComponentLog, isAppComponentLogLoading, isLoadingSummary]);

  return (
    <Layout
      wrapperClassName='w-full pt-[78px] px-[72px] ml-[100px] pc-md:ml-[256px]'
      childrenWrapperClassName='overflow-hidden'
    >
      <div className='flex flex-col mb-[28px]'>
        <Heading.H5 fontWeight={FontWeight.SEMIBOLD} className='mb-[8px]'>
          {t('TITLE')}
        </Heading.H5>
        <Heading.H9>{t('SUBTITLE')}</Heading.H9>
      </div>
      {!isAppComponentLogLoading && (
        <BalanceUpContext.Provider value={contextData}>
          <div className='w-full h-full flex pc-md:gap-[40px] gap-[32px]'>
            <MainContent
              className={`relative flex flex-col bg-bw00 rounded-[12px] w-full h-[77dvh] min-h-[431px] border-bw03 border-[1px] shadow-widgetCard ${isSubContentShown ? 'basis-[60%]' : 'basis-[100%]'}`}
            >
              <BalanceUpStepSwitch
                overdraftObject={overdraftObject}
                handleStepSubmit={handleStepSubmit}
                activeStep={activeStep}
                enrollmentType={enrollmentType}
              />
            </MainContent>

            {isSubContentShown && (
              <SubContent className='flex flex-col relative p-[32px] pc-md:p-[40px] !pb-[32px] bg-bw00 rounded-[12px] basis-[40%] h-[77dvh] border-bw03 border-[1px] shadow-widgetCard'>
                <BalanceUpDashboardSubContent />
              </SubContent>
            )}
          </div>
        </BalanceUpContext.Provider>
      )}
    </Layout>
  );
};

export default BalanceUp;
